import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { postLinkStatus } from '../redux/network/linkStatus'
import { postLinkInformation, type LinkInformationData } from '../redux/network/linkInformation'
import { resetState } from '../redux/MapDataSlice'
import { LiveJobTrackingView } from './LiveJobTrackingView'
import {
  LiveJobErrorView,
  LiveJobExpiredView,
  LiveJobInvalidLinkView,
  LiveJobLoadingView,
  LiveJobNoKeyView,
  LiveJobPendingView,
} from '../UI/LiveJob/LiveJobStatusViews'

type ViewPhase =
  | 'loading'
  | 'pending'
  | 'expired'
  | 'invalid'
  | 'error'
  | 'no_key'
  | 'loading_tracking'
  | 'tracking'
  /** Map was shown, then link Text reported pending/expired */
  | 'link_revoked'

/**
 * Public page: Live Job Tracking.
 * jobKey sources:
 * - Path: /live-job/:jobKey (preferred), e.g. /live-job/u1AI%2Bhz%2B...
 * - Query: /live-job?... (full string after "?", no param name — legacy)
 *
 * Link status API uses `Text` only (not `Status`) — see `postLinkStatus` in redux/network/linkStatus.
 */
const LiveJobPage: FC = () => {
  const location = useLocation()
  const { jobKey: jobKeyPathParam } = useParams<{ jobKey?: string }>()
  const dispatch = useDispatch()
  const [phase, setPhase] = useState<ViewPhase>('loading')
  const [errorMessage, setErrorMessage] = useState('')
  const [linkInfo, setLinkInfo] = useState<LinkInformationData | null>(null)
  const statusPollInFlight = useRef(false)

  const jobKey = useMemo(() => {
    /*if (jobKeyPathParam) {
      try {
        return decodeURIComponent(jobKeyPathParam)
      } catch {
        return jobKeyPathParam
      }
    }
    const raw = location.search
    if (!raw || raw === '?') {
      return ''
    }
    return raw.startsWith('?') ? raw.slice(1) : raw*/
    const params = new URLSearchParams(location.search)
    const queryKey = params.get('jobkey')

    if (queryKey) {
      try {
        return queryKey
      } catch {
        return queryKey
      }
    }

    return ''
  }, [jobKeyPathParam, location.search])

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      if (!jobKey.trim()) {
        setPhase('no_key')
        setErrorMessage('')
        setLinkInfo(null)
        return
      }

      setPhase('loading')
      setErrorMessage('')
      setLinkInfo(null)

      const result = await postLinkStatus(jobKey)
      if (cancelled) {
        return
      }

      switch (result.outcome) {
        case 'pending':
          setPhase('pending')
          break
        case 'expired':
          setPhase('expired')
          break
        case 'invalid':
          setPhase('invalid')
          break
        case 'error':
          setPhase('error')
          setErrorMessage(result.message)
          break
        case 'active':
          setPhase('loading_tracking')
          try {
            const info = await postLinkInformation(jobKey)
            if (cancelled) {
              return
            }
            setLinkInfo(info)
            setPhase('tracking')
          } catch (e) {
            if (cancelled) {
              return
            }
            setPhase('error')
            setErrorMessage(e instanceof Error ? e.message : 'Failed to load map data')
          }
          break
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [jobKey])

  /** While map is live: re-check link status every 60s; revoke when Text is no longer active */
  useEffect(() => {
    if (phase !== 'tracking' || !jobKey.trim()) {
      return
    }

    let effectCancelled = false

    const tick = async () => {
      if (statusPollInFlight.current) {
        return
      }
      statusPollInFlight.current = true
      try {
        const result = await postLinkStatus(jobKey)
        if (effectCancelled) {
          return
        }
        if (
          result.outcome === 'expired' ||
          result.outcome === 'pending' ||
          result.outcome === 'invalid'
        ) {
          dispatch(resetState())
          setLinkInfo(null)
          if (result.outcome === 'invalid') {
            setPhase('invalid')
          } else {
            setPhase('link_revoked')
          }
        }
      } finally {
        statusPollInFlight.current = false
      }
    }

    const intervalId = window.setInterval(tick, 60_000)
    return () => {
      effectCancelled = true
      window.clearInterval(intervalId)
      statusPollInFlight.current = false
    }
  }, [phase, jobKey, dispatch])

  if (phase === 'link_revoked') {
    return <LiveJobExpiredView />
  }

  if (phase === 'tracking' && linkInfo) {
    return <LiveJobTrackingView linkInfo={linkInfo} />
  }

  if (phase === 'loading' || phase === 'loading_tracking') {
    return (
      <LiveJobLoadingView
        message={phase === 'loading_tracking' ? 'Loading map...' : 'Checking your link...'}
      />
    )
  }

  if (phase === 'no_key') {
    return <LiveJobNoKeyView />
  }

  if (phase === 'pending') {
    return <LiveJobPendingView />
  }

  if (phase === 'expired') {
    return <LiveJobExpiredView />
  }

  if (phase === 'invalid') {
    return <LiveJobInvalidLinkView />
  }

  if (phase === 'error') {
    return <LiveJobErrorView message={errorMessage || 'Something went wrong.'} />
  }

  return <LiveJobErrorView message='Something went wrong.' />
}

export { LiveJobPage }
